import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Card, CardBody, Col, Container, Row, CardTitle,
    Input, Button, Badge, Modal, ModalHeader, ModalBody, ModalFooter, Spinner, Alert,
    Label, FormGroup
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

const Tekliflerim = () => {
    document.title = "Tekliflerim | Lojgo";
    const navigate = useNavigate();

    // --- 1. KULLANICI VE ROL KONTROLLERİ ---
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    const userId = authUser?.ID;
    const isAdmin = authUser?.Users_Type_ID === 3; // 3: Admin

    // --- 2. ADMİN İÇİN KULLANICI SEÇİM STATE'LERİ ---
    const [targetUserId, setTargetUserId] = useState("ALL"); // Admin için varsayılan "Tümü"
    const [usersList, setUsersList] = useState([]);

    // --- STATE'LER ---
    const [offers, setOffers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL"); // ALL, Bekliyor, Kabul Edildi, Reddedildi
    const [dateFilter, setDateFilter] = useState(null); // "NEW" | "OLD"

    // Modal ve İşlem State'leri
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [deletePolicy, setDeletePolicy] = useState({});

    // Kabul/Red Onay Modalları
    const [confirmModal, setConfirmModal] = useState({ open: false, offerId: null, newStatus: null });
    const [rejectModal, setRejectModal] = useState({ open: false, offerId: null });
    const [rejectReason, setRejectReason] = useState("");
    const [statusUpdating, setStatusUpdating] = useState(false);

    // --- ADMİN İSE TÜM KULLANICILARI ÇEK (Dropdown İçin) ---
    useEffect(() => {
        if (isAdmin) {
            fetch("https://hiflowapi.stechomeyazilim.info/Users?$expand=Users_Type")
                .then(res => res.json())
                .then(data => setUsersList(data.value || []))
                .catch(err => console.error("Kullanıcı listesi alınamadı:", err));
        }
    }, [isAdmin]);

    // --- API'DEN VERİ ÇEKME ---
    const fetchOffers = async () => {
        if (!userId) return;
        setIsLoading(true);
        try {
            let filterStr = "";
            if (!isAdmin) {
                // Sürücü ise sadece kendi tekliflerini görür
                filterStr = `$filter=Users_ID eq ${userId}&`;
            } else if (targetUserId !== "ALL") {
                // Admin belli bir sürücüyü seçmişse
                filterStr = `$filter=Users_ID eq ${targetUserId}&`;
            }

            // Expand: Advert(Müşteri, Lokasyon) ve Users(Teklifi veren sürücü)
            const url = `https://hiflowapi.stechomeyazilim.info/Advert_Offers?${filterStr}$expand=Users,Advert($expand=Users,Advert_Location)&$orderby=Created_DateTime desc`;
            
            const response = await fetch(url);
            const result = await response.json();
            
            if (result && result.value) {
                setOffers(result.value);
            }
        } catch (error) {
            console.error("Teklifler çekilirken hata:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchOffers();
    }, [userId, isAdmin, targetUserId]);

    // --- ADMİN: TEKLİF KABUL/RED İŞLEMİ ---
    const handleOfferStatusUpdate = (offerId, newStatus) => {
        if (newStatus === "Reddedildi") {
            setRejectReason("");
            setRejectModal({ open: true, offerId });
        } else {
            setConfirmModal({ open: true, offerId, newStatus });
        }
    };

    const applyStatusUpdate = async (offerId, newStatus, rejectionReason = null) => {
        setStatusUpdating(true);
        try {
            const payload = {
                Offer_Status: newStatus,
                ReasonForRejection: newStatus === "Reddedildi" ? (rejectionReason || null) : null,
            };
            const response = await fetch(`https://hiflowapi.stechomeyazilim.info/Advert_Offers(${offerId})`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (response.ok) {
                setOffers(prev => prev.map(o => o.ID === offerId ? { ...o, ...payload } : o));
                if (selectedOffer?.ID === offerId) {
                    setSelectedOffer(prev => ({ ...prev, ...payload }));
                }
                toast.success(`Teklif "${newStatus}" olarak güncellendi.`);
            } else {
                toast.error("Teklif durumu güncellenirken hata oluştu.");
            }
        } catch (error) {
            toast.error("Bağlantı hatası!");
        } finally {
            setStatusUpdating(false);
            setConfirmModal({ open: false, offerId: null, newStatus: null });
            setRejectModal({ open: false, offerId: null });
        }
    };

    // --- FİLTRELEME İŞLEMLERİ ---
    const filteredOffers = useMemo(() => {
        let result = [...offers];

        // 1. Arama Sorgusu
        if (query.trim()) {
            const q = query.trim().toLowerCase();
            result = result.filter((o) => {
                return (
                    o.ID?.toString().includes(q) ||
                    o.Advert_ID?.toString().includes(q) ||
                    o.Price?.toString().includes(q) ||
                    o.Users?.Users_NameSurname?.toLowerCase().includes(q) ||
                    o.Advert?.Advert_Location?.[0]?.Advert_Location_StartAddress?.toLowerCase().includes(q) ||
                    o.Advert?.Advert_Location?.[0]?.Advert_Location_FinishAddress?.toLowerCase().includes(q)
                );
            });
        }

        // 2. Durum Filtresi
        if (statusFilter !== "ALL") {
            result = result.filter(o => {
                const currentStatus = o.Offer_Status ? o.Offer_Status.trim() : "Bekliyor";
                return currentStatus === statusFilter;
            });
        }

        // 3. Tarih Sıralama
        if (dateFilter) {
            result.sort((a, b) => {
                const dateA = new Date(a.Created_DateTime || 0).getTime();
                const dateB = new Date(b.Created_DateTime || 0).getTime();
                return dateFilter === "NEW" ? dateB - dateA : dateA - dateB;
            });
        }

        return result;
    }, [offers, query, statusFilter, dateFilter]);

    const resetFilters = () => {
        setStatusFilter("ALL");
        setDateFilter(null);
        setQuery("");
    };

    // --- YARDIMCI FONKSİYONLAR ---
    const formatTime = (val) => {
        if (!val) return "--:--";
        const match = val.match(/PT(\d+)H(\d+)M/);
        if (match) return `${match[1].padStart(2, "0")}:${match[2].padStart(2, "0")}`;
        return val.slice(0, 5);
    };

    const formatDate = (val) => {
        if (!val) return "---";
        const parts = val.split("T")[0].split("-");
        return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : val;
    };

    const getDeletePolicy = (deliveryDate) => {
        if (!deliveryDate) return { canDelete: true, fee: 0, message: "Teslim tarihi bulunamadı. Ücretsiz iptal edilebilir." };
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const serviceDate = new Date(deliveryDate); serviceDate.setHours(0, 0, 0, 0);
        const dayDiff = Math.ceil((serviceDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        if (dayDiff <= 0) return { canDelete: false, fee: null, message: "Aynı gün olan teklifler iptal edilemez." };
        if (dayDiff === 1) return { canDelete: true, fee: 30, message: "Teslim tarihine 1 gün kaldığı için 30 TL iptal bedeli uygulanacaktır." };
        if (dayDiff === 2) return { canDelete: true, fee: 20, message: "Teslim tarihine 2 gün kaldığı için 20 TL iptal bedeli uygulanacaktır." };
        return { canDelete: true, fee: 0, message: "Bu teklif ücretsiz iptal edilebilir." };
    };

    const askDeleteOffer = (offer) => {
        const policy = getDeletePolicy(offer.Delivery_Date);
        setDeletePolicy(policy);
        setModalVisible(false);
        setDeleteModalVisible(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedOffer || !deletePolicy.canDelete) return;
        try {
            await fetch(`https://hiflowapi.stechomeyazilim.info/Advert_Offers(${selectedOffer.ID})`, { method: "DELETE" });
            setDeleteModalVisible(false);
            setSelectedOffer(null);
            fetchOffers();
            toast.success("Teklif başarıyla silindi.");
        } catch (error) {
            console.error("Teklif silme hatası:", error);
            toast.error("Teklif silinemedi!");
        }
    };

    return (
        <React.Fragment>
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Görevlerim" breadcrumbItem="Tekliflerim" />

                    {/* SADECE ADMİNLER İÇİN KULLANICI SEÇİM ALANI */}
                    {isAdmin && (
                        <Row className="mb-4">
                            <Col md={12} lg={8} xl={6}>
                                <Card className="shadow-sm border border-info rounded-4 mb-0">
                                    <CardBody className="py-3 px-4">
                                        <Label className="fw-bold text-info mb-2 d-flex align-items-center">
                                            <i className="mdi mdi-shield-account fs-4 me-2"></i> Admin Paneli: Sürücü Filtresi
                                        </Label>
                                        <Input 
                                            type="select" 
                                            value={targetUserId} 
                                            onChange={(e) => setTargetUserId(e.target.value)} 
                                            className="bg-soft-info border-info fw-bold text-dark cursor-pointer"
                                        >
                                            <option value="ALL">Tüm Platformdaki Teklifleri Göster</option>
                                            {usersList.map(u => (
                                                <option key={u.ID} value={u.ID}>
                                                    {u.Users_NameSurname || u.Users_Name} - ({u.Users_Type?.Users_Type_Title || "Bilinmeyen Rol"})
                                                </option>
                                            ))}
                                        </Input>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}
                    
                    <Row>
                        {/* SOL BÖLÜM: FİLTRELER */}
                        <Col lg="3">
                            <Card>
                                <CardBody>
                                    <CardTitle className="mb-4">Filtreleme</CardTitle>
                                    
                                    <div>
                                        <h5 className="font-size-14 mb-3">Arama</h5>
                                        <div className="search-box position-relative mb-4">
                                            <Input
                                                type="text"
                                                className="form-control border-light bg-light"
                                                placeholder={isAdmin ? "İlan no, konum, sürücü adı..." : "İlan no, konum, tutar..."}
                                                value={query}
                                                onChange={(e) => setQuery(e.target.value)}
                                            />
                                            <i className="mdi mdi-magnify search-icon" style={{position: 'absolute', top: '10px', left: '10px'}} />
                                        </div>
                                    </div>

                                    <div className="mt-4 pt-3 border-top">
                                        <h5 className="font-size-14 mb-3">Teklif Durumu</h5>
                                        <div className="d-flex flex-column gap-2">
                                            <div className="form-check">
                                                <Input type="radio" name="status" id="statusAll" checked={statusFilter === "ALL"} onChange={() => setStatusFilter("ALL")} />
                                                <Label className="form-check-label" htmlFor="statusAll">Tümü</Label>
                                            </div>
                                            <div className="form-check">
                                                <Input type="radio" name="status" id="statusWait" checked={statusFilter === "Bekliyor"} onChange={() => setStatusFilter("Bekliyor")} />
                                                <Label className="form-check-label" htmlFor="statusWait">Bekliyor</Label>
                                            </div>
                                            <div className="form-check">
                                                <Input type="radio" name="status" id="statusApprove" checked={statusFilter === "Kabul Edildi"} onChange={() => setStatusFilter("Kabul Edildi")} />
                                                <Label className="form-check-label text-success" htmlFor="statusApprove">Kabul Edildi</Label>
                                            </div>
                                            <div className="form-check">
                                                <Input type="radio" name="status" id="statusReject" checked={statusFilter === "Reddedildi"} onChange={() => setStatusFilter("Reddedildi")} />
                                                <Label className="form-check-label text-danger" htmlFor="statusReject">Reddedildi</Label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-4 pt-3 border-top">
                                        <h5 className="font-size-14 mb-3">Tarih Sıralaması</h5>
                                        <div className="d-flex gap-2">
                                            <Button color={dateFilter === "NEW" ? "primary" : "light"} size="sm" onClick={() => setDateFilter(dateFilter === "NEW" ? null : "NEW")}>
                                                En Yeni
                                            </Button>
                                            <Button color={dateFilter === "OLD" ? "primary" : "light"} size="sm" onClick={() => setDateFilter(dateFilter === "OLD" ? null : "OLD")}>
                                                En Eski
                                            </Button>
                                        </div>
                                    </div>

                                    {(statusFilter !== "ALL" || dateFilter !== null || query !== "") && (
                                        <div className="mt-4 pt-3 border-top text-center">
                                            <Button color="danger" outline size="sm" onClick={resetFilters}>
                                                <i className="mdi mdi-close me-1"></i> Filtreleri Temizle
                                            </Button>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        {/* SAĞ BÖLÜM: TEKLİF KARTLARI LİSTESİ */}
                        <Col lg="9">
                            {isLoading ? (
                                <div className="text-center py-5">
                                    <Spinner color="primary" />
                                    <p className="text-muted mt-2">Teklifler yükleniyor...</p>
                                </div>
                            ) : filteredOffers.length === 0 ? (
                                <Card className="text-center py-5 shadow-none border bg-light">
                                    <CardBody>
                                        <i className="mdi mdi-text-box-search-outline display-4 text-muted mb-3"></i>
                                        <h5 className="fw-bold">Teklif Bulunamadı</h5>
                                        <p className="text-muted">Arama kriterlerinize uygun veya henüz verilmiş bir teklif yok.</p>
                                    </CardBody>
                                </Card>
                            ) : (
                                <Row>
                                    {filteredOffers.map((item, key) => {
                                        const location = item.Advert?.Advert_Location?.[0];
                                        let statusColor = "warning";
                                        if (item.Offer_Status === "Kabul Edildi") statusColor = "success";
                                        if (item.Offer_Status === "Reddedildi") statusColor = "danger";
                                        
                                        return (
                                            <Col xl={6} key={key}>
                                                <Card className="shadow-sm border">
                                                    <CardBody>
                                                        {/* Üst Kısım: Kimlik, Durum ve Tarih */}
                                                        <div className="d-flex justify-content-between align-items-start mb-3 border-bottom pb-3">
                                                            <div>
                                                                <Badge className="bg-soft-primary text-primary px-2 py-1 mb-2 font-size-12">
                                                                    Teklif #{item.ID}
                                                                </Badge>
                                                                <Badge className={`bg-soft-${statusColor} text-${statusColor} px-2 py-1 ms-2 font-size-12`}>
                                                                    {item.Offer_Status || "Bekliyor"}
                                                                </Badge>
                                                                
                                                                {/* SADECE ADMİN GÖRÜR: Sürücü Bilgisi */}
                                                                {isAdmin && item.Users && (
                                                                    <div className="mt-2 font-size-13 text-muted">
                                                                        <i className="mdi mdi-account-tie text-primary me-1"></i> 
                                                                        Sürücü: <strong>{item.Users.Users_NameSurname || "Bilinmiyor"}</strong>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="text-muted font-size-12 fw-medium">
                                                                <i className="mdi mdi-calendar me-1"></i>
                                                                {formatDate(item.Created_DateTime)}
                                                            </div>
                                                        </div>

                                                        {/* Orta Kısım: Rota */}
                                                        <div className="bg-light p-3 rounded mb-3">
                                                            <div className="d-flex align-items-center mb-2">
                                                                <i className="mdi mdi-circle-slice-8 text-primary font-size-12 me-2"></i>
                                                                <span className="text-dark fw-medium text-truncate">
                                                                    {location?.Advert_Location_StartAddress || "Başlangıç belirtilmedi"}
                                                                </span>
                                                            </div>
                                                            <div className="border-start border-2 border-secondary ms-1 my-1 ps-3" style={{height: "10px"}}></div>
                                                            <div className="d-flex align-items-center">
                                                                <i className="mdi mdi-map-marker text-danger font-size-14 me-2"></i>
                                                                <span className="text-dark fw-medium text-truncate">
                                                                    {location?.Advert_Location_FinishAddress || "Bitiş belirtilmedi"}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Reddedildiyse Neden Göster */}
                                                        {item.Offer_Status === "Reddedildi" && item.ReasonForRejection && (
                                                            <div className="text-danger font-size-12 mb-3 bg-soft-danger p-2 rounded border border-danger">
                                                                <strong>Red Nedeni:</strong> {item.ReasonForRejection}
                                                            </div>
                                                        )}

                                                        {/* Alt Kısım: Fiyat ve Butonlar */}
                                                        <div className="d-flex justify-content-between align-items-end mt-3">
                                                            <div>
                                                                <h4 className="text-dark fw-bold mb-1">{item.Price?.toLocaleString("tr-TR")} TL</h4>
                                                                <p className="text-muted font-size-12 mb-0 fw-medium">
                                                                    <i className="mdi mdi-road-variant me-1"></i> 
                                                                    {location?.Km ? `${location.Km} km` : "-- km"}
                                                                </p>
                                                            </div>
                                                            
                                                            {/* Aksiyon Butonları (Admin vs Driver) */}
                                                            <div className="d-flex gap-2 flex-wrap justify-content-end">
                                                                {isAdmin && (
                                                                    <>
                                                                        <Button color="success" size="sm" outline disabled={item.Offer_Status === "Kabul Edildi"} onClick={() => handleOfferStatusUpdate(item.ID, "Kabul Edildi")}>
                                                                            <i className="mdi mdi-check"></i> Kabul Et
                                                                        </Button>
                                                                        <Button color="danger" size="sm" outline disabled={item.Offer_Status === "Reddedildi"} onClick={() => handleOfferStatusUpdate(item.ID, "Reddedildi")}>
                                                                            <i className="mdi mdi-close"></i> Reddet
                                                                        </Button>
                                                                    </>
                                                                )}
                                                                <Button color="light" size="sm" className="text-primary fw-bold px-3" onClick={() => { setSelectedOffer(item); setModalVisible(true); }}>
                                                                    Detayı Gör <i className="mdi mdi-chevron-right"></i>
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            )}
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* --- DETAY MODALI --- */}
            <Modal isOpen={modalVisible} toggle={() => setModalVisible(!modalVisible)} size="lg" centered scrollable>
                <ModalHeader toggle={() => setModalVisible(!modalVisible)} className="bg-light border-bottom">
                    <span className="fw-bold">Teklif Detayları</span>
                </ModalHeader>
                <ModalBody className="p-4 bg-white">
                    {selectedOffer && (
                        <>
                            {isAdmin && (
                                <>
                                    <h6 className="text-muted fw-bold text-uppercase mb-3 font-size-11">TEKLİFİ VEREN SÜRÜCÜ</h6>
                                    <Card className="shadow-none border border-info bg-soft-info mb-4">
                                        <CardBody className="p-3">
                                            <Row>
                                                <Col sm={6}><i className="mdi mdi-account-tie text-info me-2"></i> Ad Soyad: <strong>{selectedOffer.Users?.Users_NameSurname || "Bilinmiyor"}</strong></Col>
                                                <Col sm={6}><i className="mdi mdi-identifier text-info me-2"></i> Sürücü ID: <strong>#{selectedOffer.Users_ID}</strong></Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                </>
                            )}

                            {/* Müşteri Bilgileri */}
                            <h6 className="text-muted fw-bold text-uppercase mb-3 font-size-11">MÜŞTERİ BİLGİLERİ</h6>
                            <Card className="shadow-none border bg-light mb-4">
                                <CardBody className="p-3">
                                    <Row>
                                        <Col sm={4} className="mb-2 mb-sm-0"><i className="mdi mdi-account-circle text-primary me-2"></i> Ad Soyad: <strong className="float-end">{selectedOffer.Advert?.Users?.Users_NameSurname || "Bilinmiyor"}</strong></Col>
                                        <Col sm={4} className="mb-2 mb-sm-0"><i className="mdi mdi-phone text-primary me-2"></i> Telefon: <strong className="float-end text-primary text-decoration-underline">{selectedOffer.Advert?.Users?.Users_PhoneNumber || "Bilinmiyor"}</strong></Col>
                                        <Col sm={4}><i className="mdi mdi-tag-text-outline text-primary me-2"></i> Müşteri No: <strong className="float-end">#{selectedOffer.Advert?.Users?.ID}</strong></Col>
                                    </Row>
                                </CardBody>
                            </Card>

                            {/* İlan ve Zamanlama */}
                            <h6 className="text-muted fw-bold text-uppercase mb-3 font-size-11">İLAN VE ZAMANLAMA</h6>
                            <Card className="shadow-none border bg-light mb-4">
                                <CardBody className="p-3">
                                    <Row>
                                        <Col sm={4} className="mb-2 mb-sm-0"><i className="mdi mdi-bullhorn-outline text-success me-2"></i> İlan No: <strong className="float-end">#{selectedOffer.Advert_ID}</strong></Col>
                                        <Col sm={4} className="mb-2 mb-sm-0"><i className="mdi mdi-calendar-check text-success me-2"></i> Teslimat Tarihi: <strong className="float-end">{formatDate(selectedOffer.Delivery_Date)}</strong></Col>
                                        <Col sm={4}><i className="mdi mdi-clock-outline text-success me-2"></i> Saat Aralığı: <strong className="float-end">{formatTime(selectedOffer.Start_Time)} - {formatTime(selectedOffer.End_Time)}</strong></Col>
                                    </Row>
                                </CardBody>
                            </Card>

                            {/* Rota Detayı */}
                            <h6 className="text-muted fw-bold text-uppercase mb-3 font-size-11">ROTA DETAYI</h6>
                            <Card className="shadow-none border bg-light mb-4">
                                <CardBody className="p-3">
                                    <div className="d-flex mb-2"><i className="mdi mdi-circle-slice-8 text-primary me-2 mt-1"></i> <div><span className="text-muted d-block font-size-11">Başlangıç</span> <strong>{selectedOffer.Advert?.Advert_Location?.[0]?.Advert_Location_StartAddress}</strong></div></div>
                                    <div className="d-flex mb-2"><i className="mdi mdi-map-marker text-danger me-2 mt-1"></i> <div><span className="text-muted d-block font-size-11">Bitiş</span> <strong>{selectedOffer.Advert?.Advert_Location?.[0]?.Advert_Location_FinishAddress}</strong></div></div>
                                    <div className="d-flex"><i className="mdi mdi-road-variant text-secondary me-2 mt-1"></i> <div><span className="text-muted d-block font-size-11">Mesafe</span> <strong>{selectedOffer.Advert?.Advert_Location?.[0]?.Km} km</strong></div></div>
                                </CardBody>
                            </Card>

                            {/* Fiyatlandırma */}
                            <h6 className="text-muted fw-bold text-uppercase mb-3 font-size-11">FİYATLANDIRMA</h6>
                            <Card className="shadow-none border border-primary bg-soft-primary mb-4">
                                <CardBody className="p-3">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-dark fw-medium"><i className="mdi mdi-car-back me-2"></i>Yol Masrafı</span>
                                        <strong>{selectedOffer.Travel_Expenses} TL</strong>
                                    </div>
                                    <div className="d-flex justify-content-between pb-3 border-bottom border-light">
                                        <span className="text-dark fw-medium"><i className="mdi mdi-wallet-outline me-2"></i>Hizmet Bedeliniz</span>
                                        <strong>{selectedOffer.Own_Offer} TL</strong>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <h5 className="text-primary mb-0 fw-bold">TOPLAM TEKLİF</h5>
                                        <h4 className="text-primary mb-0 fw-bold">{selectedOffer.Price?.toLocaleString("tr-TR")} TL</h4>
                                    </div>
                                    
                                    {selectedOffer.Offer_Status === "Reddedildi" && selectedOffer.ReasonForRejection && (
                                        <div className="mt-3 p-2 bg-white text-danger border border-danger rounded text-center font-size-13">
                                            <strong>Red Nedeni:</strong> {selectedOffer.ReasonForRejection}
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </>
                    )}
                </ModalBody>
                <ModalFooter className="bg-light border-top">
                    <div className="w-100 d-flex justify-content-between align-items-center">
                        {/* Sol Taraf: Admin Butonları veya Kullanıcı Silme Butonu */}
                        {isAdmin ? (
                            <div className="d-flex gap-2">
                                <Button color="success" disabled={selectedOffer?.Offer_Status === "Kabul Edildi"} onClick={() => handleOfferStatusUpdate(selectedOffer.ID, "Kabul Edildi")}>
                                    <i className="mdi mdi-check me-1"></i> Kabul Et
                                </Button>
                                <Button color="danger" outline disabled={selectedOffer?.Offer_Status === "Reddedildi"} onClick={() => handleOfferStatusUpdate(selectedOffer.ID, "Reddedildi")}>
                                    <i className="mdi mdi-close me-1"></i> Reddet
                                </Button>
                            </div>
                        ) : (
                            <Button color="danger" outline onClick={() => askDeleteOffer(selectedOffer)}>
                                <i className="mdi mdi-trash-can-outline me-1"></i> Teklifi Sil
                            </Button>
                        )}
                        
                        {/* Sağ Taraf: Kapat / Güncelle */}
                        <div className="d-flex gap-2">
                            <Button color="secondary" outline onClick={() => setModalVisible(false)}>Kapat</Button>
                            {!isAdmin && (
                                <Button color="primary" onClick={() => navigate(`/offer-update/${selectedOffer.Advert_ID}`)}>
                                    <i className="mdi mdi-pencil me-1"></i> Teklifi Güncelle
                                </Button>
                            )}
                        </div>
                    </div>
                </ModalFooter>
            </Modal>

            {/* --- SİLME ONAY MODALI (İptal Politikası - Sadece Kullanıcı İçin) --- */}
            <Modal isOpen={deleteModalVisible} toggle={() => setDeleteModalVisible(!deleteModalVisible)} centered>
                <ModalHeader toggle={() => setDeleteModalVisible(!deleteModalVisible)} className="bg-danger text-white">
                    <span className="text-white"><i className="mdi mdi-alert-circle-outline me-2"></i> İptal Onayı</span>
                </ModalHeader>
                <ModalBody className="p-4 text-center">
                    <div className="mb-4">
                        <i className={`mdi ${deletePolicy.canDelete ? "mdi-trash-can-outline text-danger" : "mdi-cancel text-warning"} display-4`}></i>
                    </div>
                    <h5 className="fw-bold mb-3">{deletePolicy.canDelete ? "Teklifi silmek istediğinize emin misiniz?" : "Bu Görev İptal Edilemez"}</h5>
                    <Alert color={deletePolicy.canDelete && deletePolicy.fee > 0 ? "danger" : "info"} className="border-0 font-size-14 fw-medium text-start">
                        {deletePolicy.message}
                    </Alert>
                </ModalBody>
                <ModalFooter className="border-top-0 justify-content-center pb-4">
                    <Button color="light" className="px-4" onClick={() => setDeleteModalVisible(false)}>
                        Vazgeç
                    </Button>
                    {deletePolicy.canDelete && (
                        <Button color="danger" className="px-4" onClick={handleDeleteConfirm}>
                            Evet, Sil
                        </Button>
                    )}
                </ModalFooter>
            </Modal>

            {/* --- KABUL ONAY MODALI --- */}
            <Modal isOpen={confirmModal.open} toggle={() => setConfirmModal({ open: false, offerId: null, newStatus: null })} centered>
                <ModalHeader toggle={() => setConfirmModal({ open: false, offerId: null, newStatus: null })} className="bg-success text-white">
                    <span className="text-white"><i className="mdi mdi-check-circle-outline me-2"></i> Teklifi Kabul Et</span>
                </ModalHeader>
                <ModalBody className="p-4 text-center">
                    <i className="mdi mdi-check-circle text-success display-4 mb-3"></i>
                    <h5 className="fw-bold">Bu teklifi kabul etmek istediğinize emin misiniz?</h5>
                    <p className="text-muted">Teklif <strong className="text-success">Kabul Edildi</strong> olarak işaretlenecek.</p>
                </ModalBody>
                <ModalFooter className="justify-content-center border-top-0 pb-4">
                    <Button color="light" className="px-4" onClick={() => setConfirmModal({ open: false, offerId: null, newStatus: null })}>
                        Vazgeç
                    </Button>
                    <Button color="success" className="px-4" disabled={statusUpdating} onClick={() => applyStatusUpdate(confirmModal.offerId, confirmModal.newStatus)}>
                        {statusUpdating ? <Spinner size="sm" /> : <><i className="mdi mdi-check me-1"></i> Evet, Kabul Et</>}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* --- RED NEDENİ MODALI --- */}
            <Modal isOpen={rejectModal.open} toggle={() => setRejectModal({ open: false, offerId: null })} centered>
                <ModalHeader toggle={() => setRejectModal({ open: false, offerId: null })} className="bg-danger text-white">
                    <span className="text-white"><i className="mdi mdi-close-circle-outline me-2"></i> Teklifi Reddet</span>
                </ModalHeader>
                <ModalBody className="p-4">
                    <p className="text-muted mb-3">Bu teklifi reddetmek üzeresiniz. İsterseniz bir red nedeni girebilirsiniz.</p>
                    <FormGroup>
                        <Label className="fw-bold">Red Nedeni <span className="text-muted fw-normal">(isteğe bağlı)</span></Label>
                        <Input
                            type="textarea"
                            rows={3}
                            placeholder="Örn: Fiyat yüksek, uygun araç yok..."
                            value={rejectReason}
                            onChange={e => setRejectReason(e.target.value)}
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter className="justify-content-end border-top">
                    <Button color="light" className="px-4" onClick={() => setRejectModal({ open: false, offerId: null })}>
                        Vazgeç
                    </Button>
                    <Button color="danger" className="px-4" disabled={statusUpdating} onClick={() => applyStatusUpdate(rejectModal.offerId, "Reddedildi", rejectReason)}>
                        {statusUpdating ? <Spinner size="sm" /> : <><i className="mdi mdi-close me-1"></i> Reddet</>}
                    </Button>
                </ModalFooter>
            </Modal>

        </React.Fragment>
    );
};

export default Tekliflerim;